import React, { useEffect, useState } from "react";
import { DimensionamentoService } from "../../services/DimensionamentoServices";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import RootCauseTableReport from "../TableReport/RootCauseTableReport";
import RootCauseTableRow from "../TableReport/RootCauseTableRow";
import { FaTrash } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import { PiProhibit } from "react-icons/pi";
import Swal from "sweetalert2";
import StatusBarChart from "../StatusBarChart/StatusBarChart";

const RootCauseReport = ({
  rowData,
  causeAndActionRowData,
  editExistent,
  viewOnly,
}) => {
  const [data, setData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [cityData, setCityData] = useState([]);
  const [selectedCity, setSelectedCity] = useState("Nan");
  const [selectedState, setSelectedState] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editableRows, setEditableRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [networkFilter, setNetworkFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const FIELDS_TO_COMPARE = [
    "cause",
    "action",
    "responsible",
    "initialDate",
    "finalDate",
    "status",
  ];

  const networkFilters = [
    { value: "all", label: "Todos" },
    { value: "excess", label: "Rede Excedente" },
    { value: "insufficient", label: "Insuficiência de Rede" },
  ];

  const statusFilters = [
    { value: "all", label: "Todos os status" },
    { value: "NOT_STARTED", label: "Não iniciado" },
    { value: "IN_PROGRESS", label: "Em andamento" },
    { value: "COMPLETED", label: "Concluído" },
    { value: "CANCELLED", label: "Cancelado" },
    { value: "ON_HOLD", label: "Em espera" },
    { value: "UNDER_REVIEW", label: "Em revisão" },
  ];

  const statusOptions = [
    { name: "Não iniciado", value: "NOT_STARTED" },
    { name: "Em andamento", value: "IN_PROGRESS" },
    { name: "Concluído", value: "COMPLETED" },
    { name: "Cancelado", value: "CANCELLED" },
    { name: "Em espera", value: "ON_HOLD" },
    { name: "Em revisão", value: "UNDER_REVIEW" },
  ];

  const statusNames = [
    { name: "Não iniciado", value: "NOT_STARTED" },
    { name: "Em andamento", value: "IN_PROGRESS" },
    { name: "Concluído", value: "COMPLETED" },
    { name: "Cancelado", value: "CANCELLED" },
    { name: "Em espera", value: "ON_HOLD" },
    { name: "Em revisão", value: "UNDER_REVIEW" },
  ];

  const statusColorMap = {
    NOT_STARTED: "#9ca3af",
    IN_PROGRESS: "#3b82f6",
    COMPLETED: "#22c55e",
    CANCELLED: "#ef4444",
    ON_HOLD: "#facc15",
    UNDER_REVIEW: "#8b5cf6"
  };

  useEffect(() => {
    const filterData = () => {
      if (!originalData) return;
      let filtered = [...originalData];

      if (searchTerm) {
        filtered = filtered.filter(
          (item) =>
            item.attribute.attribute.referenceCode?.includes(searchTerm) ||
            item.attribute.attribute.name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item.serviceType
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item.cause?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.responsible?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (networkFilter !== "all") {
        filtered = filtered.filter((item) =>
          networkFilter === "excess"
            ? item.description === "Rede Excedente"
            : item.description === "Insuficiência de Rede"
        );
      }

      if (statusFilter !== "all") {
        filtered = filtered.filter((item) => item.status === statusFilter);
      }

      setData(filtered);

      if (editMode) {
        const filteredEditableRows = filtered.map((item) => ({
          referenceCode:
            item.referenceCode || item.attribute?.attribute?.referenceCode,
          parameterName: item.parameterName || item.attribute?.attribute?.name,
          serviceType: item.serviceType,
          city: item.cityObj || item.attribute?.city,
          description: item.networkSufficiency || item.description,
          cause: item.cause,
          action: item.action,
          responsible: item.responsible,
          initialDate: item.initialDate,
          finalDate: item.finalDate,
          status: item.status,
        }));
        setEditableRows(filteredEditableRows);
      }
    };

    filterData();
  }, [searchTerm, networkFilter, statusFilter, originalData, editMode]);

  const fetchCities = async () => {
    try {
      const cities = await DimensionamentoService.getCitiesWithCauseAndActionReport();
      setCityData(cities);
    } catch (error) {
      console.error("Erro ao buscar cidades:", error);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchCities();
  }, [rowData]);

  useEffect(() => {
    const fetchReport = async () => {
      if (editExistent) {
        setData(causeAndActionRowData);
        setOriginalData(causeAndActionRowData);
        setEditableRows(
          causeAndActionRowData.map((item) => ({
            referenceCode:
              causeAndActionRowData[0].attribute.attribute.referenceCode,
            parameterName: causeAndActionRowData[0].attribute.attribute.name,
            serviceType: causeAndActionRowData[0].serviceType,
            city: item.attribute.city,
            description: item.description,
            cause: item.cause,
            action: item.action,
            responsible: item.responsible,
            initialDate: item.initialDate,
            finalDate: item.finalDate,
            status: item.status,
          }))
        );
        setHasChanges(false);
        return;
      }
      if (selectedCity && selectedState) {
        try {
          const report = await DimensionamentoService.getCauseAndActionReport(
            selectedCity,
            selectedState
          );
          setData(report);
          setOriginalData(JSON.parse(JSON.stringify(report)));
          setEditableRows(
            report.map((item) => ({
              referenceCode: item.referenceCode,
              parameterName: item.parameterName,
              serviceType: item.serviceType,
              city: item.cityObj,
              description: item.networkSufficiency,
              cause: item.cause,
              action: item.action,
              responsible: item.responsible,
              initialDate: item.initialDate,
              finalDate: item.finalDate,
              status: item.status,
            }))
          );
          setHasChanges(false);
        } catch (error) {
          console.error("Erro ao buscar relatório de causa raiz:", error);
        }
      } else {
        setData(null);
        setOriginalData(null);
        setHasChanges(false);
      }
    };
    fetchReport();
  }, [selectedCity, selectedState, causeAndActionRowData, editExistent]);

  const handleCityChange = (event) => {
    const cityName = event.target.value;

    if (cityName === "Nan") {
      setSelectedCity("Nan");
      setSelectedState("");
      setData(null);
      setOriginalData(null);
      return;
    }

    const cityObj = cityData.find((city) => city.city === cityName);
    if (cityObj) {
      setSelectedCity(cityObj.city);
      setSelectedState(cityObj.state);
    }
  };
  const toggleEditMode = () => {
    if (editMode && hasChanges) {
      setEditableRows(
        originalData.map((item) => ({
          referenceCode: item.referenceCode,
          parameterName: item.parameterName,
          serviceType: item.serviceType,
          city: item.cityObj,
          description: item.networkSufficiency,
          cause: item.cause,
          action: item.action,
          responsible: item.responsible,
          initialDate: item.initialDate,
          finalDate: item.finalDate,
          status: item.status,
        }))
      );
      setData(JSON.parse(JSON.stringify(originalData)));
      setHasChanges(false);
    } else if (!editMode) {
      setEditableRows(
        data.map((item) => ({
          referenceCode:
            item.referenceCode || item.attribute?.attribute?.referenceCode,
          parameterName: item.parameterName || item.attribute?.attribute?.name,
          serviceType: item.serviceType,
          city: item.cityObj || item.attribute?.city,
          description: item.networkSufficiency || item.description,
          cause: item.cause,
          action: item.action,
          responsible: item.responsible,
          initialDate: item.initialDate,
          finalDate: item.finalDate,
          status: item.status,
        }))
      );
    }

    setEditMode(!editMode);
  };
  const deleteLine = (index) => {
    const updatedEditableRows = [...editableRows];
    updatedEditableRows.splice(index, 1);
    setEditableRows(updatedEditableRows);

    const updatedData = [...data];
    updatedData.splice(index, 1);
    setData(updatedData);
    setHasChanges(true);
  };

  const saveChanges = async () => {
    const isAnyFieldEmpty = editableRows.some(
      (row) =>
        !row.cause ||
        !row.action ||
        !row.responsible ||
        !row.initialDate ||
        !row.finalDate ||
        !row.status
    );

    if (isAnyFieldEmpty) {
      await Swal.fire({
        icon: "warning",
        title: "Campos obrigatórios não preenchidos",
        text: "Por favor, preencha todos os campos antes de salvar.",
        confirmButtonText: "Ok",
      });
      return;
    }

    const invalidDateRow = editableRows.find(
      (row) =>
        row.initialDate &&
        row.finalDate &&
        row.finalDate <= row.initialDate
    );

    if (invalidDateRow) {
      await Swal.fire({
        icon: "warning",
        title: "Datas inválidas",
        text: "A data de término deve ser pelo menos 1 dia após a data de início.",
        confirmButtonText: "Ok",
      });
      return;
    }

    let dataToSave;

    if (editExistent) {
      const city = causeAndActionRowData[0].attribute.city.city;
      const state = causeAndActionRowData[0].attribute.city.state;

      try {
        const allCityData =
          await DimensionamentoService.getCauseAndActionReport(city, state);

        const editedItem = editableRows[0];
        const originalItem = originalData[0];
        dataToSave = allCityData.map((item) => {
          if (
            item.attribute.attribute.referenceCode ===
              originalItem.attribute.attribute.referenceCode &&
            item.serviceType === originalItem.serviceType &&
            item.description === originalItem.description
          ) {
            return {
              referenceCode: item.attribute.attribute.referenceCode,
              parameterName: item.attribute.attribute.name,
              city: item.attribute.city,
              description: item.description,
              serviceType: item.serviceType,
              cause: editedItem.cause,
              action: editedItem.action,
              responsible: editedItem.responsible,
              initialDate: editedItem.initialDate,
              finalDate: editedItem.finalDate,
              status: editedItem.status,
            };
          } else {
            return {
              referenceCode: item.attribute.attribute.referenceCode,
              parameterName: item.attribute.attribute.name,
              city: item.attribute.city,
              description: item.description,
              serviceType: item.serviceType,
              cause: item.cause,
              action: item.action,
              responsible: item.responsible,
              initialDate: item.initialDate,
              finalDate: item.finalDate,
              status: item.status,
            };
          }
        });
      } catch (error) {
        console.error("Erro ao buscar dados completos:", error);
        return;
      }
    } else {
      if (searchTerm || networkFilter !== "all" || statusFilter !== "all") {
        dataToSave = originalData.map((originalItem) => {
          const editedIndex = data.findIndex(
            (filteredItem) =>
              (filteredItem.attribute?.attribute?.referenceCode ||
                filteredItem.referenceCode) ===
                (originalItem.attribute?.attribute?.referenceCode ||
                  originalItem.referenceCode) &&
              filteredItem.serviceType === originalItem.serviceType &&
              (filteredItem.description || filteredItem.networkSufficiency) ===
                (originalItem.description || originalItem.networkSufficiency)
          );

          if (editedIndex !== -1) {
            const editedRow = editableRows[editedIndex];
            return {
              referenceCode:
                originalItem.attribute?.attribute?.referenceCode ||
                originalItem.referenceCode,
              parameterName:
                originalItem.attribute?.attribute?.name ||
                originalItem.parameterName,
              city: originalItem.attribute?.city || originalItem.cityObj,
              description:
                originalItem.description || originalItem.networkSufficiency,
              serviceType: originalItem.serviceType,
              cause: editedRow.cause,
              action: editedRow.action,
              responsible: editedRow.responsible,
              initialDate: editedRow.initialDate,
              finalDate: editedRow.finalDate,
              status: editedRow.status,
            };
          } else {
            return {
              referenceCode:
                originalItem.attribute?.attribute?.referenceCode ||
                originalItem.referenceCode,
              parameterName:
                originalItem.attribute?.attribute?.name ||
                originalItem.parameterName,
              city: originalItem.attribute?.city || originalItem.cityObj,
              description:
                originalItem.description || originalItem.networkSufficiency,
              serviceType: originalItem.serviceType,
              cause: originalItem.cause,
              action: originalItem.action,
              responsible: originalItem.responsible,
              initialDate: originalItem.initialDate,
              finalDate: originalItem.finalDate,
              status: originalItem.status,
            };
          }
        });
      } else {
        dataToSave = editableRows.map((row, index) => {
          const dataItem = data[index];
          return {
            referenceCode:
              dataItem.attribute?.attribute?.referenceCode ||
              dataItem.referenceCode,
            parameterName:
              dataItem.attribute?.attribute?.name || dataItem.parameterName,
            city: dataItem.attribute?.city || dataItem.cityObj,
            description: dataItem.description || dataItem.networkSufficiency,
            serviceType: dataItem.serviceType,
            cause: row.cause,
            action: row.action,
            responsible: row.responsible,
            initialDate: row.initialDate,
            finalDate: row.finalDate,
            status: row.status,
          };
        });
      }
    }
    const city =
      selectedCity === "Nan"
        ? causeAndActionRowData[0].attribute.city.city
        : selectedCity;
    const state =
      selectedCity === "Nan"
        ? causeAndActionRowData[0].attribute.city.state
        : selectedState;

    try {
      await DimensionamentoService.updateCauseAndActionReport(
        dataToSave,
        city,
        state
      );

      if (editExistent) {
        setOriginalData(JSON.parse(JSON.stringify(data)));
      } else {
        try {
          const updatedReport =
            await DimensionamentoService.getCauseAndActionReport(city, state);
          setData(updatedReport);
          setOriginalData(JSON.parse(JSON.stringify(updatedReport)));
          setEditableRows(
            updatedReport.map((item) => ({
              referenceCode: item.referenceCode,
              parameterName: item.parameterName,
              serviceType: item.serviceType,
              city: item.cityObj,
              description: item.networkSufficiency,
              cause: item.cause,
              action: item.action,
              responsible: item.responsible,
              initialDate: item.initialDate,
              finalDate: item.finalDate,
              status: item.status,
            }))
          );
        } catch (refreshError) {
          console.error("Erro ao recarregar dados:", refreshError);
          setOriginalData(JSON.parse(JSON.stringify(data)));
        }
      }

      setHasChanges(false);
      setEditMode(false);
      await Swal.fire({
        icon: "success",
        title: "Alterações salvas com sucesso!",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error("Erro ao salvar alterações:", error);
    }
  };

  const areEditableRowsEqual = (updated, original) => {
    if (updated.length !== original.length) return false;

    for (let i = 0; i < updated.length; i++) {
      const filteredUpdated = FIELDS_TO_COMPARE.reduce((acc, field) => {
        acc[field] = updated[i][field];
        return acc;
      }, {});

      const filteredOriginal = FIELDS_TO_COMPARE.reduce((acc, field) => {
        acc[field] = original[i][field];
        return acc;
      }, {});

      if (
        JSON.stringify(filteredUpdated) !== JSON.stringify(filteredOriginal)
      ) {
        return false;
      }
    }
    return true;
  };
  const handleChange = (index, field, value) => {
    const updated = [...editableRows];
    updated[index][field] = value;
    setEditableRows(updated);

    setData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: value,
      };

      const isEqual = areEditableRowsEqual(updated, originalData);
      setHasChanges(!isEqual);
      return newData;
    });
  };

  const cancelChanges = () => {
    setEditableRows(
      originalData.map((item) => ({
        referenceCode: item.referenceCode,
        parameterName: item.parameterName,
        serviceType: item.serviceType,
        city: item.cityObj,
        description: item.networkSufficiency,
        cause: item.cause,
        action: item.action,
        responsible: item.responsible,
        initialDate: item.initialDate,
        finalDate: item.finalDate,
        status: item.status,
      }))
    );
    setData(JSON.parse(JSON.stringify(originalData)));
    setHasChanges(false);
  };

  const formatDateToShow = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${day}/${month}/${year}`;
  };

  const formatDateToInput = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getEndDateStyle = (finalDate, status) => {
    if (!finalDate || status === "COMPLETED") {
      return {};
    }

    const currentDate = new Date();
    let endDate;

    if (typeof finalDate === "string") {
      endDate = new Date(finalDate);
    } else if (typeof finalDate === "number") {
      endDate = new Date(finalDate);
    } else {
      return {};
    }

    if (isNaN(endDate.getTime())) {
      return {};
    }

    const oneWeekBefore = new Date(endDate);
    oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);

    currentDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    oneWeekBefore.setHours(0, 0, 0, 0);

    if (currentDate > endDate) {
      return {
        backgroundColor: "#fef2f2",
        color: "#dc2626",
        borderLeft: "3px solid #dc2626",
        fontWeight: 500,
      };
    } else if (currentDate >= oneWeekBefore && currentDate <= endDate) {
      return {
        backgroundColor: "#fff7ed",
        color: "#ea580c",
        borderLeft: "3px solid #ea580c",
        fontWeight: 500,
      };
    }

    return {};
  };

  useEffect(() => {}, [data]);

  return (
    <div className="w-full flex flex-col items-center justify-center p-6">
      {(editExistent || viewOnly) && (
        <h1 className="text-lg font-medium text-center">
          {causeAndActionRowData[0].attribute.city.city} -{" "}
          {causeAndActionRowData[0].attribute.city.state}
        </h1>
      )}
      {rowData ? (
        <>
          <h1 className="text-lg font-medium text-center">
            {rowData.city} - {rowData.state}
          </h1>
          <div className="pt-10 w-full h-full space-y-6 pb-6">
            <RootCauseTableReport
              data={rowData}
              causeAndActionData={causeAndActionRowData}
            />
          </div>
        </>
      ) : data ? (
        <>
          {!editExistent && (
            <>
              <FormControl
                variant="outlined"
                sx={{
                  maxWidth: 350,
                  width: "100%",
                  marginBottom: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&:hover fieldset": {
                      borderColor: "#059669",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#059669",
                    },
                  },
                }}
              >
                <InputLabel sx={{ fontSize: "0.875rem" }}>Município</InputLabel>
                <Select
                  variant="outlined"
                  value={selectedCity}
                  onChange={handleCityChange}
                  label="Município"
                  sx={{ fontSize: "0.875rem" }}
                >
                  <MenuItem value="Nan" sx={{ fontSize: "0.875rem" }}>
                    Selecione um município
                  </MenuItem>
                  {cityData
                    .slice()
                    .sort((a, b) => a.city.localeCompare(b.city, 'pt-BR'))
                    .map((c, i) => (
                      <MenuItem
                        key={i}
                        value={c.city}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {c.city}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 w-full items-center justify-between mb-6">
                <TextField
                  label="Buscar por Código, Parâmetro, Tipo Prestador, Causa, Ação ou Responsável"
                  variant="outlined"
                  sx={{
                    minWidth: "60%",
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      "&:hover fieldset": {
                        borderColor: "#059669",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#059669",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <FormControl
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#059669",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#059669",
                      },
                    },
                  }}
                >
                  <InputLabel sx={{ fontSize: "0.875rem" }}>
                    Descrição
                  </InputLabel>
                  <Select
                    variant="outlined"
                    value={networkFilter}
                    onChange={(e) => setNetworkFilter(e.target.value)}
                    label="Descrição"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {networkFilters.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#059669",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#059669",
                      },
                    },
                  }}
                >
                  <InputLabel sx={{ fontSize: "0.875rem" }}>Status</InputLabel>
                  <Select
                    variant="outlined"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {statusFilters.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </>
          )}
          <div className="pt-6 w-full h-full space-y-6 pb-6">
            <TableContainer
              component={Paper}
              sx={{
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
                overflowX: "auto",
                position: "relative",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f6f6f6",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Código
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Parâmetro
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Tipo Prestador
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Descrição
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Causa
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Ação
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Responsável
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Data Início
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Data Término
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Status
                    </TableCell>
                    {!viewOnly &&
                      (data.length > 0 || (data.length === 0 && editMode)) && (
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#ffffff",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: "#374151",
                            py: 1.5,
                            borderBottom: "1px solid #f0f0f0",
                            borderLeft: "2px solid #e5e7eb",
                            textAlign: "center",
                            zIndex: 2,
                            boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          {!editMode ? (
                            <Tooltip title="Habilitar edição" arrow>
                              <IconButton
                                onClick={() => toggleEditMode()}
                                sx={{
                                  color: "#2563eb",
                                  backgroundColor: "#eff6ff",
                                  border: "1px solid #bfdbfe",
                                  borderRadius: "6px",
                                  padding: "6px",
                                  "&:hover": {
                                    backgroundColor: "#dbeafe",
                                    borderColor: "#2563eb",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <MdEdit size={14} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Cancelar edição" arrow>
                              <IconButton
                                onClick={() => toggleEditMode()}
                                sx={{
                                  color: "#dc2626",
                                  backgroundColor: "#fef2f2",
                                  border: "1px solid #fecaca",
                                  borderRadius: "6px",
                                  padding: "6px",
                                  "&:hover": {
                                    backgroundColor: "#fecaca",
                                    borderColor: "#dc2626",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <PiProhibit size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={editMode ? 11 : 10}
                        sx={{ textAlign: "center" }}
                      >
                        Nenhum dado encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#fafafa",
                          },
                          borderBottom:
                            index === data.length - 1
                              ? "none"
                              : "1px solid #f0f0f0",
                        }}
                      >
                        {editMode ? (
                          <>
                            <TableCell
                              sx={{
                                minWidth: "95px",
                                fontSize: "0.875rem",
                                color: "#1f2937",
                                py: 2,
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              {item.attribute.attribute.referenceCode}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: "110px",
                                fontSize: "0.875rem",
                                color: "#1f2937",
                                py: 2,
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              {item.attribute.attribute.name}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: "95px",
                                fontSize: "0.875rem",
                                color: "#1f2937",
                                py: 2,
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              {item.serviceType || "Prestador Desconhecido"}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: "115px",
                                fontSize: "0.875rem",
                                color: "#1f2937",
                                py: 2,
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              {item.description}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: "175px",
                                padding: "8px 4px",
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              <TextField
                                placeholder="Descreva a causa..."
                                value={editableRows[index]?.cause || ""}
                                onChange={(e) => handleChange(index, "cause", e.target.value)}
                                multiline
                                inputProps={{ maxLength: 500 }}
                                maxRows={3}
                                fullWidth
                                size="small"
                                helperText={`${(editableRows[index]?.cause || "").length}/500 caracteres`}
                                FormHelperTextProps={{
                                  sx: { fontSize: "0.75rem", textAlign: "right", color: "#6b7280" },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fafafa",
                                    borderRadius: "6px",
                                    fontSize: "0.875rem",
                                    "& textarea": {
                                      padding: "8px 2px",
                                      overflowY: "auto",
                                      resize: "none",
                                    },
                                    "& fieldset": { border: "1px solid #e5e7eb" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#059669",
                                      borderWidth: "1px",
                                    },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: "175px", padding: "8px 4px", borderBottom: "1px solid #f0f0f0" }}>
                              <TextField
                                placeholder="Descreva a ação..."
                                value={editableRows[index]?.action || ""}
                                onChange={(e) => handleChange(index, "action", e.target.value)}
                                multiline
                                inputProps={{ maxLength: 500 }}
                                maxRows={3}
                                fullWidth
                                size="small"
                                helperText={`${(editableRows[index]?.action || "").length}/500 caracteres`}
                                FormHelperTextProps={{
                                  sx: { fontSize: "0.75rem", textAlign: "right", color: "#6b7280" },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fafafa",
                                    borderRadius: "6px",
                                    fontSize: "0.875rem",
                                    "& textarea": { padding: "8px 2px", overflowY: "auto", resize: "none" },
                                    "& fieldset": { border: "1px solid #e5e7eb" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "1px" },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: "175px", padding: "8px 4px", borderBottom: "1px solid #f0f0f0" }}>
                              <TextField
                                placeholder="Informe um responsável..."
                                value={editableRows[index]?.responsible || ""}
                                onChange={(e) => handleChange(index, "responsible", e.target.value)}
                                multiline
                                inputProps={{ maxLength: 100 }}
                                maxRows={3}
                                fullWidth
                                size="small"
                                helperText={`${(editableRows[index]?.responsible || "").length}/100 caracteres`}
                                FormHelperTextProps={{
                                  sx: { fontSize: "0.75rem", textAlign: "right", color: "#6b7280" },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fafafa",
                                    borderRadius: "6px",
                                    fontSize: "0.875rem",
                                    "& textarea": { padding: "8px 2px", overflowY: "auto", resize: "none" },
                                    "& fieldset": { border: "1px solid #e5e7eb" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "1px" },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: "130px", padding: "8px 4px", borderBottom: "1px solid #f0f0f0" }}>
                              <TextField
                                type="date"
                                value={formatDateToInput(editableRows[index]?.initialDate)}
                                onChange={(e) =>
                                  handleChange(index, "initialDate", new Date(e.target.value).getTime())
                                }
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fafafa",
                                    borderRadius: "6px",
                                    fontSize: "0.875rem",
                                    "& input": { padding: "8px 12px" },
                                    "& fieldset": { border: "1px solid #e5e7eb" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "1px" },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: "130px",
                                padding: "8px 4px",
                                borderBottom: "1px solid #f0f0f0",
                                ...getEndDateStyle(editableRows[index]?.finalDate, editableRows[index]?.status),
                              }}
                            >
                              <TextField
                                type="date"
                                value={formatDateToInput(editableRows[index]?.finalDate)}
                                onChange={(e) =>
                                  handleChange(index, "finalDate", new Date(e.target.value).getTime())
                                }
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={
                                  editableRows[index]?.initialDate &&
                                  editableRows[index]?.finalDate &&
                                  editableRows[index]?.finalDate <= editableRows[index]?.initialDate
                                }
                                helperText={
                                  editableRows[index]?.initialDate &&
                                  editableRows[index]?.finalDate &&
                                  editableRows[index]?.finalDate <= editableRows[index]?.initialDate
                                    ? "Término deve ser ao menos 1 dia após início"
                                    : ""
                                }
                                FormHelperTextProps={{ sx: { fontSize: "0.75rem", color: "#dc2626" } }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fafafa",
                                    borderRadius: "6px",
                                    fontSize: "0.875rem",
                                    "& input": { padding: "8px 12px" },
                                    "& fieldset": { border: "1px solid #e5e7eb" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "1px" },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: "130px",
                                padding: "8px 4px",
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              <Select
                                variant="outlined"
                                value={editableRows[index]?.status || ""}
                                onChange={(e) => handleChange(index, "status", e.target.value)}
                                fullWidth
                                size="small"
                                displayEmpty
                                renderValue={(selected) => {
                                  if (!selected) {
                                    return (
                                      <span
                                        style={{
                                          display: "inline-block",
                                          padding: "2px 6px",
                                          borderRadius: "4px",
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                          backgroundColor: statusColorMap[selected] || "#9ca3af",
                                          color: "#fff",
                                          maxWidth: "120px",
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        Informe um status...
                                      </span>
                                    );
                                  }
                                  const option = statusOptions.find((o) => o.value === selected);
                                  return (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        backgroundColor: statusColorMap[selected] || "#9ca3af",
                                        color: "#fff",
                                        maxWidth: "120px",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {option?.name ?? selected}
                                    </span>
                                  );
                                }}
                                sx={{
                                  backgroundColor: "#fafafa",
                                  borderRadius: "6px",
                                  fontSize: "0.875rem",
                                  "& .MuiSelect-select": { padding: "8px 12px", whiteSpace: "normal" },
                                  "& fieldset": { border: "1px solid #e5e7eb" },
                                  "&:hover fieldset": { borderColor: "#9ca3af" },
                                  "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "1px" },
                                }}
                              >
                                <MenuItem value="" sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                                  <em>Informe um status...</em>
                                </MenuItem>
                                {statusOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.875rem" }}>
                                    {option.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                backgroundColor: "#ffffff",
                                textAlign: "center",
                                borderBottom: "1px solid #f0f0f0",
                                borderLeft: "2px solid #e5e7eb",
                                py: 2,
                                zIndex: 1,
                                boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.05)",
                              }}
                            >
                              <Tooltip title="Excluir item" arrow>
                                <IconButton
                                  onClick={() => deleteLine(index)}
                                  sx={{
                                    color: "#dc2626",
                                    backgroundColor: "#fef2f2",
                                    border: "1px solid #fecaca",
                                    borderRadius: "6px",
                                    padding: "6px",
                                    "&:hover": {
                                      backgroundColor: "#fecaca",
                                      borderColor: "#dc2626",
                                      transform: "scale(1.05)",
                                    },
                                    transition: "all 0.2s ease-in-out",
                                  }}
                                >
                                  <FaTrash size={12} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </>
                        ) : (
                          <RootCauseTableRow
                            item={item}
                            index={index}
                            viewOnly={viewOnly}
                            statusNames={statusNames}
                            formatDateToShow={formatDateToShow}
                            deleteLine={deleteLine}
                          />
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          {hasChanges && (
            <div className="flex space-x-3 mb-6 w-full justify-end mt-6">
              <Button
                variant="contained"
                onClick={saveChanges}
                sx={{
                  backgroundColor: "#059669",
                  color: "white",
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  boxShadow:
                    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                  "&:hover": {
                    backgroundColor: "#047857",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  },
                }}
              >
                Salvar Alterações
              </Button>
              <Button
                variant="outlined"
                onClick={cancelChanges}
                sx={{
                  color: "#6b7280",
                  borderColor: "#d1d5db",
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                Cancelar
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <FormControl
            variant="outlined"
            sx={{
              width: 350,
              marginBottom: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "&:hover fieldset": {
                  borderColor: "#059669",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#059669",
                },
              },
            }}
          >
            <InputLabel sx={{ fontSize: "0.875rem" }}>Município</InputLabel>
            <Select
              variant="outlined"
              value={selectedCity}
              onChange={handleCityChange}
              label="Município"
              sx={{ fontSize: "0.875rem" }}
            >
              <MenuItem value="Nan" sx={{ fontSize: "0.875rem" }}>
                Selecione um município
              </MenuItem>
              {cityData
                .slice()
                .sort((a, b) => a.city.localeCompare(b.city, 'pt-BR'))
                .map((c, i) => (
                  <MenuItem key={i} value={c.city} sx={{ fontSize: "0.875rem" }}>
                    {c.city}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <div className="flex justify-center items-center h-full">
            <h2 className="text-lg font-medium text-gray-600">
              Escolha um município para carregar o relatório de causa raiz.
            </h2>
          </div>
        </>
      )}
      {data && data.length > 0 && (
        <div className="flex flex-col items-center md:flex-row w-full gap-y-4 md:gap-y-0 md:space-x-6">
          <StatusBarChart data={data} usePercentage={false}/>
          <StatusBarChart data={data} usePercentage={true} />
        </div>

      )}
    </div>
  );
};

export default RootCauseReport;
